/**
 * Layout-level handler for the `appointment.imminent` SSE event.
 * Registers with RealtimeProvider (which owns the single SSE
 * connection) and renders the toast banner.
 *
 * Was previously opening its own stream; now it only subscribes to
 * one event type via `useRegisterEventHandler` — the actual stream
 * is owned by `RealtimeProvider`.
 */
import { useAppointmentReminder } from '../../hooks/useAppointmentReminder';
import { useAuthStore } from '../../stores/authStore';
import AppointmentReminderBanner from '../scheduling/AppointmentReminderBanner';
import { useRegisterEventHandler } from './RealtimeProvider';

type Role = 'agent' | 'client' | 'manager' | 'super_admin' | null;

function viewerForRole(role: Role): 'agent' | 'client' {
  return role === 'client' ? 'client' : 'agent';
}

export default function LayoutRealtimeBus(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const role = (user?.role as Role | undefined) ?? null;

  const { handler, lastToast, dismissToast } = useAppointmentReminder(viewerForRole(role));
  useRegisterEventHandler('appointment.imminent', handler);

  if (!role) return null;
  return <AppointmentReminderBanner toast={lastToast} onDismiss={dismissToast} />;
}
